import { formatDate } from "@plasma/utils/time";

import type {
  DeviceTransaction,
  EmployeeReportData,
  ReasonTracking,
  SalesSummary,
} from "./employee-report-types";
import { calculateItemsTotal } from "./employee-report-types";

// Employee Report Header Component
export function EmployeeReportHeader({
  locationName,
  address,
  currentDate,
  isPreviewData,
  isLoadingReport,
  reportError,
  hasRealData,
}: {
  locationName: string;
  address: {
    addressLine1: string;
    addressLine2: string | null;
    city: string;
    state: string;
    zipCode: number;
  } | null;
  currentDate: Date;
  isPreviewData: boolean;
  isLoadingReport: boolean;
  reportError: Error | null;
  hasRealData: boolean;
}) {
  return (
    <div className="text-center">
      END OF DAY REPORT: EMPLOYEE
      <br />
      {locationName}
      <br />
      {address ? (
        <>
          {address.addressLine1}
          {address.addressLine2 && `, ${address.addressLine2}`}
          <br />
          {address.city}, {address.state} {address.zipCode}
        </>
      ) : (
        "No address provided"
      )}
      <br />
      {formatDate(currentDate || new Date(), "MMMM Do, YYYY")}
      {isPreviewData && (
        <div className="caption bg-warning/10 text-warning mt-2 rounded-sm px-2 py-1">
          No data found for this date.
        </div>
      )}
      {isLoadingReport && (
        <div className="caption bg-info/10 text-info mt-2 rounded-sm px-2 py-1">
          Loading report data...
        </div>
      )}
      {reportError && (
        <div className="caption bg-destructive/10 text-destructive mt-2 rounded-sm px-2 py-1">
          Error loading report: Failed to load report
        </div>
      )}
      {hasRealData && !isLoadingReport && !reportError && (
        <div className="caption bg-success/10 text-success mt-2 rounded-sm px-2 py-1">
          Showing actual data
        </div>
      )}
    </div>
  );
}

// Employee Info Section
export function EmployeeInfoSection({
  employee,
  employeeId,
  showDividers,
}: {
  employee: { firstName: string; lastName: string } | null;
  employeeId: string | null;
  showDividers: boolean;
}) {
  return (
    <>
      {showDividers && <hr className="border-border my-2 border-t" />}
      <div className="text-center">
        <div className="font-bold">
          {employee
            ? `${employee.firstName} ${employee.lastName}`
            : "Employee Report"}
        </div>
        {employeeId && (
          <div className="caption text-muted-foreground">ID: {employeeId}</div>
        )}
      </div>
      {showDividers && <hr className="border-border my-2 border-t" />}
    </>
  );
}

// Employee Hours Section
export function EmployeeHoursSection({
  hoursWorked,
  weekHours,
  showDividers,
}: {
  hoursWorked: string;
  weekHours: string;
  showDividers: boolean;
}) {
  return (
    <div>
      <div className="text-center font-bold">EMPLOYEE HOURS</div>
      <div className="flex justify-between">
        <span>Total Hours Day</span>
        <span>{(Number(hoursWorked) || 0).toFixed(2)}</span>
      </div>
      <div className="flex justify-between">
        <span>Total Hours Week (Sat - Sun)</span>
        <span>{(Number(weekHours) || 0).toFixed(2)}</span>
      </div>
      {showDividers && <hr className="border-border my-2 border-t" />}
    </div>
  );
}

// Employee Tips Section
export function EmployeeTipsSection({
  cashTips,
  cardTips,
  totalTips,
  showDividers,
}: {
  cashTips: string;
  cardTips: string;
  totalTips: string;
  showDividers: boolean;
}) {
  return (
    <div>
      <div className="text-center font-bold">EMPLOYEE TIPS</div>
      <div className="flex justify-between">
        <span>Cash Tips</span>
        <span>${cashTips ?? "0.00"}</span>
      </div>
      <div className="flex justify-between">
        <span>Card Tips</span>
        <span>${cardTips ?? "0.00"}</span>
      </div>
      <div className="flex justify-between">
        <span>Total Employee Tips</span>
        <span>${totalTips ?? "0.00"}</span>
      </div>
      {showDividers && <hr className="border-border my-2 border-t" />}
    </div>
  );
}

// Orders and Bills Summary Section
export function EmployeeOrdersBillsSection({
  ordersCreated,
  billsClosed,
  averageBillAmount,
  showDividers,
}: {
  ordersCreated: number;
  billsClosed: number;
  averageBillAmount: string;
  showDividers: boolean;
}) {
  return (
    <div>
      <div className="text-center font-bold">ORDERS AND BILLS SUMMARY</div>
      <div className="flex justify-between">
        <span>Orders Created</span>
        <span>{ordersCreated ?? 0}</span>
      </div>
      <div className="flex justify-between">
        <span>Bills Closed</span>
        <span>{billsClosed ?? 0}</span>
      </div>
      <div className="flex justify-between">
        <span>Avg. Bill Amount</span>
        <span>${averageBillAmount ?? "0.00"}</span>
      </div>
      {showDividers && <hr className="border-border my-2 border-t" />}
    </div>
  );
}

// Sales Categories Section for Employee Report
export function EmployeeSalesCategoriesSection({
  salesByCategory,
  totalSales,
  showDividers,
}: {
  salesByCategory: SalesSummary[] | undefined;
  totalSales: string;
  showDividers: boolean;
}) {
  return (
    <div>
      <div className="text-center font-bold">SALES CATEGORIES</div>
      <div className="flex justify-between">
        <span>CATEGORY</span>
        <div className="flex justify-end space-x-2">
          <span>QUANTITY</span>
          <span>NET SALES</span>
        </div>
      </div>
      {salesByCategory?.map((category: SalesSummary) => (
        <div className="flex justify-between" key={category.categoryId}>
          <span className="grow">{category.categoryName}</span>
          <div className="flex justify-end space-x-2">
            <span>{category.quantity}</span>
            <span>${category.amount}</span>
          </div>
        </div>
      ))}
      <div className="flex justify-between font-bold">
        <span>Total Net Sales</span>
        <div className="flex justify-end space-x-2">
          <span>${totalSales ?? "0.00"}</span>
        </div>
      </div>
      {showDividers && <hr className="border-border my-2 border-t" />}
    </div>
  );
}

// Devices Summary Section for Employee Report
export function EmployeeDevicesSummarySection({
  deviceTransactions,
  startCash,
  showDividers,
}: {
  deviceTransactions: DeviceTransaction;
  startCash: string;
  showDividers: boolean;
}) {
  return (
    <div>
      <div className="text-center font-bold">DEVICES SUMMARY</div>
      <div className="flex justify-between">
        <span className="font-bold underline">
          POS DEVICE {deviceTransactions.inputByEmployeeId}
        </span>
        <span />
      </div>
      <div className="flex justify-between">
        <span>Cash Day Start</span>
        <span>${startCash ?? "0.00"}</span>
      </div>
      <div className="flex justify-between">
        <span className="pl-4 italic">
          Input By: {deviceTransactions.inputByEmployeeId}
        </span>
        <span />
      </div>
      <div className="flex justify-between">
        <span>Cash Collected</span>
        <span>${deviceTransactions.cashCollected}</span>
      </div>
      <div className="flex justify-between">
        <span>Cash Sales</span>
        <span>${deviceTransactions.cashSales}</span>
      </div>
      <div className="flex justify-between">
        <span>Cash Deposits</span>
        <span>${deviceTransactions.cashDeposits}</span>
      </div>
      <div className="flex justify-between">
        <span>Cash Withdrawals</span>
        <span>${deviceTransactions.cashWithdrawals}</span>
      </div>
      <div className="flex justify-between">
        <span>Difference</span>
        <span>${deviceTransactions.difference}</span>
      </div>
      {showDividers && <hr className="border-border my-2 border-t" />}
    </div>
  );
}

// Reason Tracking Section (Voided, Discounted, Refunded items)
export function EmployeeReasonTrackingSection({
  title,
  items,
  totalLabel,
  keyPrefix,
  showManagerCodes,
  showDividers,
}: {
  title: string;
  items: ReasonTracking[];
  totalLabel: string;
  keyPrefix: string;
  showManagerCodes: boolean;
  showDividers: boolean;
}) {
  return (
    <div>
      <div className="text-center font-bold">{title}</div>
      {items.map((item: ReasonTracking) => (
        <div
          className="flex justify-between"
          key={`${keyPrefix}-${item.reason}-${item.amount}`}
        >
          <span>{item.reason}</span>
          <span>${item.amount}</span>
        </div>
      ))}
      <div className="flex justify-between font-bold">
        <span className="pl-4">{totalLabel}</span>
        <span>${calculateItemsTotal(items)}</span>
      </div>
      {showManagerCodes &&
        items.map((item: ReasonTracking) => (
          <div
            className="flex justify-between"
            key={`${keyPrefix}-code-${item.reason}-${item.permissionCode.employeeId}`}
          >
            <span className="italic">
              Permission Code ({item.permissionCode.employeeName})
            </span>
            <span>{item.permissionCode.employeeId}</span>
          </div>
        ))}
      {showDividers && <hr className="border-border my-2 border-t" />}
    </div>
  );
}

// Employee Report Preview (receipt preview panel)
export function EmployeeReportPreview({
  location,
  currentDate,
  isPreviewData,
  isLoadingReport,
  reportError,
  hasRealData,
  employee,
  employeeId,
  displayData,
  showEmployeeHours,
  showEmployeeTips,
  showOrdersAndBillsSummary,
  showDividers,
  showManagerCodes,
  shouldShowSalesCategories,
  shouldShowDevices,
  shouldShowVoided,
  shouldShowDiscounted,
  shouldShowRefunded,
}: {
  location: {
    name: string;
    address: {
      addressLine1: string;
      addressLine2: string | null;
      city: string;
      state: string;
      zipCode: number;
    } | null;
  };
  currentDate: Date;
  isPreviewData: boolean;
  isLoadingReport: boolean;
  reportError: Error | null;
  hasRealData: boolean;
  employee: { firstName: string; lastName: string } | null;
  employeeId: string | null;
  displayData: EmployeeReportData;
  showEmployeeHours: boolean;
  showEmployeeTips: boolean;
  showOrdersAndBillsSummary: boolean;
  showDividers: boolean;
  showManagerCodes: boolean;
  shouldShowSalesCategories: boolean;
  shouldShowDevices: boolean;
  shouldShowVoided: boolean;
  shouldShowDiscounted: boolean;
  shouldShowRefunded: boolean;
}) {
  return (
    <div className="bg-background-subtle rounded-lg p-6">
      <div className="caption bg-background text-foreground max-h-dvh w-96 overflow-y-auto rounded-md px-6 py-8 font-mono shadow-md">
        <EmployeeReportHeader
          address={location.address}
          currentDate={currentDate}
          hasRealData={hasRealData}
          isLoadingReport={isLoadingReport}
          isPreviewData={isPreviewData}
          locationName={location.name}
          reportError={reportError}
        />

        <EmployeeInfoSection
          employee={employee}
          employeeId={employeeId}
          showDividers={showDividers}
        />

        {showEmployeeHours && (
          <EmployeeHoursSection
            hoursWorked={displayData.hoursWorked}
            showDividers={showDividers}
            weekHours={displayData.weekHours}
          />
        )}

        {showEmployeeTips && (
          <EmployeeTipsSection
            cardTips={displayData.cardTips}
            cashTips={displayData.cashTips}
            showDividers={showDividers}
            totalTips={displayData.totalTips}
          />
        )}

        {showOrdersAndBillsSummary && (
          <EmployeeOrdersBillsSection
            averageBillAmount={displayData.averageBillAmount}
            billsClosed={displayData.billsClosed}
            ordersCreated={displayData.ordersCreated}
            showDividers={showDividers}
          />
        )}

        {shouldShowSalesCategories && (
          <EmployeeSalesCategoriesSection
            salesByCategory={displayData.salesByCategory}
            showDividers={showDividers}
            totalSales={displayData.totalSales}
          />
        )}

        {shouldShowDevices && displayData.deviceTransactions && (
          <EmployeeDevicesSummarySection
            deviceTransactions={displayData.deviceTransactions}
            showDividers={showDividers}
            startCash={displayData.startCash}
          />
        )}

        {shouldShowVoided && (
          <EmployeeReasonTrackingSection
            items={displayData.voidedItems}
            keyPrefix="void"
            showDividers={showDividers}
            showManagerCodes={showManagerCodes}
            title="VOIDED ITEMS"
            totalLabel="Voided Items Totals"
          />
        )}

        {shouldShowDiscounted && (
          <EmployeeReasonTrackingSection
            items={displayData.discountedItems}
            keyPrefix="discount"
            showDividers={showDividers}
            showManagerCodes={showManagerCodes}
            title="DISCOUNTED ITEMS"
            totalLabel="Discounted Items Totals"
          />
        )}

        {shouldShowRefunded && (
          <EmployeeReasonTrackingSection
            items={displayData.refundedItems}
            keyPrefix="refund"
            showDividers={showDividers}
            showManagerCodes={showManagerCodes}
            title="REFUNDED ITEMS"
            totalLabel="Refunded Items Total"
          />
        )}
      </div>
    </div>
  );
}
